/**
 * Definition Validation Utilities
 *
 * Validates collaboration definitions and returns per-node errors.
 */

import {
  CollaborationDefinition,
  CollaborationNode,
  isAgentNode,
  isFunctionNode,
  isBranchNode,
} from '../types/definition';

export interface ValidationError {
  nodeId: string;
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  nodeErrors: Map<string, ValidationError[]>;
}

/**
 * Validate a collaboration definition
 */
export const validateDefinition = (definition: CollaborationDefinition): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  const nodeErrors = new Map<string, ValidationError[]>();

  const addError = (nodeId: string, field: string, message: string, severity: 'error' | 'warning' = 'error') => {
    const error: ValidationError = { nodeId, field, message, severity };
    if (severity === 'error') {
      errors.push(error);
    } else {
      warnings.push(error);
    }

    const existing = nodeErrors.get(nodeId) || [];
    existing.push(error);
    nodeErrors.set(nodeId, existing);
  };

  const nodes = definition.nodes || [];
  const edges = definition.edges || [];
  const nodeIds = new Set(nodes.map(n => n.id));

  // Build edge maps for connectivity analysis
  const incomingEdges = new Map<string, string[]>();
  const outgoingEdges = new Map<string, string[]>();

  nodes.forEach(n => {
    incomingEdges.set(n.id, []);
    outgoingEdges.set(n.id, []);
  });

  edges.forEach(edge => {
    if (nodeIds.has(edge.from) && nodeIds.has(edge.to)) {
      outgoingEdges.get(edge.from)?.push(edge.to);
      incomingEdges.get(edge.to)?.push(edge.from);
    }
  });

  // Validate each node
  nodes.forEach(node => {
    validateNode(node, nodeIds, addError);
  });

  // Check for entry nodes
  const entryNodes = nodes.filter(n => n.entry === true);
  const topologicalEntries = nodes.filter(n => (incomingEdges.get(n.id)?.length || 0) === 0);

  if (entryNodes.length === 0 && topologicalEntries.length === 0) {
    // No explicit entry and no topological entry - add warning to first node or general
    if (nodes.length > 0) {
      addError(nodes[0].id, 'entry', 'No entry point defined. Mark a node as entry or ensure at least one node has no incoming edges.', 'warning');
    }
  }

  // Check for orphaned nodes (no edges at all)
  nodes.forEach(node => {
    const hasIncoming = (incomingEdges.get(node.id)?.length || 0) > 0;
    const hasOutgoing = (outgoingEdges.get(node.id)?.length || 0) > 0;
    const isEntry = node.entry === true;

    if (!hasIncoming && !hasOutgoing && nodes.length > 1) {
      addError(node.id, 'edges', 'Node is not connected to any other nodes', 'warning');
    } else if (!hasIncoming && !isEntry && nodes.length > 1) {
      // Node has no incoming edges but is not marked as entry
      const isTopologicalEntry = topologicalEntries.some(n => n.id === node.id);
      if (!isTopologicalEntry || entryNodes.length > 0) {
        addError(node.id, 'edges', 'Node has no incoming edges. Consider marking it as an entry point.', 'warning');
      }
    }
  });

  // Validate template expressions reference valid nodes
  nodes.forEach(node => {
    validateTemplateReferences(node, nodeIds, addError);
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    nodeErrors,
  };
};

/**
 * Validate a single node based on its type
 */
const validateNode = (
  node: CollaborationNode,
  validNodeIds: Set<string>,
  addError: (nodeId: string, field: string, message: string, severity?: 'error' | 'warning') => void
) => {
  // Validate agent nodes
  if (isAgentNode(node)) {
    if (!node.agentId || node.agentId.trim() === '') {
      addError(node.id, 'agentId', 'Agent must be selected');
    }
  }

  // Validate function nodes
  if (isFunctionNode(node)) {
    if (!node.function || node.function.trim() === '') {
      addError(node.id, 'function', 'Function must be selected');
    }
  }

  // Validate branch nodes
  if (isBranchNode(node)) {
    if (!node.branchType) {
      addError(node.id, 'branchType', 'Branch type must be selected');
    }

    if (node.branchType === 'conditional') {
      /**
       * A conditional branch routes EITHER by a top-level `condition` or by
       * `decisions[]` (task 774). Requiring the top-level form alone reported
       * every seeded branch as broken — the engine's own routing form was
       * being called invalid by the editor.
       *
       * A decision with no `condition` is the engine's explicit DEFAULT arm, so
       * it is not itself a defect; what makes the branch routable is at least
       * one arm that actually decides something.
       */
      const decisions = node.decisions ?? [];
      const routesByCondition = Boolean(node.condition && node.condition.trim() !== '');
      const conditionalDecisions = decisions.filter(
        (decision) => decision.condition && decision.condition.trim() !== ''
      );

      if (!routesByCondition && conditionalDecisions.length === 0) {
        addError(
          node.id,
          'condition',
          decisions.length > 0
            ? 'Conditional branch needs a condition: none of its decisions carry one'
            : 'Conditional branch requires a condition, or decisions that route on one'
        );
      }

      // A decision pointing nowhere is a dead arm, whichever form routes it.
      decisions.forEach((decision, index) => {
        if (!decision.targetNodeId || !validNodeIds.has(decision.targetNodeId)) {
          addError(
            node.id,
            `decisions[${index}].targetNodeId`,
            `Decision "${decision.name || decision.id || index}" targets unknown node: ${
              decision.targetNodeId || '(none)'
            }`
          );
        }
      });
    }

    if (node.branchType === 'loop' && !node.until) {
      addError(node.id, 'until', 'Loop branch requires an until condition');
    }
  }
};

/**
 * Validate template expressions like {{nodes.nodeId.field}}
 */
const validateTemplateReferences = (
  node: CollaborationNode,
  validNodeIds: Set<string>,
  addError: (nodeId: string, field: string, message: string, severity?: 'error' | 'warning') => void
) => {
  const templatePattern = /\{\{nodes\.([^.}]+)\.[^}]+\}\}/g;

  const checkValue = (value: unknown, field: string) => {
    if (typeof value === 'string') {
      let match;
      while ((match = templatePattern.exec(value)) !== null) {
        const referencedNodeId = match[1];
        if (!validNodeIds.has(referencedNodeId)) {
          addError(node.id, field, `References non-existent node: ${referencedNodeId}`, 'error');
        }
      }
    } else if (typeof value === 'object' && value !== null) {
      Object.entries(value).forEach(([, v]) => checkValue(v, field));
    }
  };

  if (node.input) {
    checkValue(node.input, 'input');
  }
  if (node.output) {
    checkValue(node.output, 'output');
  }
};

/**
 * Get validation errors for a specific node
 */
export const getNodeErrors = (
  nodeId: string,
  validationResult: ValidationResult
): ValidationError[] => {
  return validationResult.nodeErrors.get(nodeId) || [];
};

/**
 * Check if a node has any errors (not warnings)
 */
export const nodeHasErrors = (
  nodeId: string,
  validationResult: ValidationResult
): boolean => {
  const nodeErrs = validationResult.nodeErrors.get(nodeId) || [];
  return nodeErrs.some(e => e.severity === 'error');
};

/**
 * Check if a node has any warnings
 */
export const nodeHasWarnings = (
  nodeId: string,
  validationResult: ValidationResult
): boolean => {
  const nodeErrs = validationResult.nodeErrors.get(nodeId) || [];
  return nodeErrs.some(e => e.severity === 'warning');
};
